import { AlertTriangle, X } from 'lucide-react'
import { useEffect, type ReactNode } from 'react'

import { tt } from '@agos/shared'

import { cn } from '../cn'
import { BUTTON_DANGER, BUTTON_GHOST, BUTTON_PRIMARY, BUTTON_SECONDARY } from './button-classes'

interface ConfirmDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'danger' | 'default'
  loading?: boolean
  icon?: ReactNode
  /**
   * Rendered between the question and the two buttons — `FRONTEND.md` §9.76.
   *
   * For a confirmation that needs something *from* the person rather than only their assent: §10A.20
   * requires a typed reason before an invoice may be pulled out of a SEPA batch. Absent on every
   * other caller, which keeps the plain yes/no dialog exactly as it was.
   */
  children?: ReactNode
  /** Refuse the confirm until `children` is satisfied — the button, not a message after the press. */
  confirmDisabled?: boolean
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  cancelLabel,
  variant = 'default',
  loading,
  icon,
  children,
  confirmDisabled,
}: ConfirmDialogProps) {
  // FB-007: legacy has no keyboard escape. On a POS tablet a modal that only
  // closes by tapping a target is a modal that traps a busy waiter.
  useEffect(() => {
    if (!open) return
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading) onClose()
    }
    document.addEventListener('keydown', closeOnEscape)
    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [open, loading, onClose])

  if (!open) return null

  const isDanger = variant === 'danger'

  return (
    <div
      role="dialog"
      aria-modal="true"
      // Named, so a screen reader announces which question is being asked — and so a page with a
      // composer open behind its confirmation has two distinguishable dialogs rather than two
      // anonymous ones.
      aria-label={title}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md p-6"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div
            className={cn(
              'w-10 h-10 rounded-xl flex items-center justify-center shrink-0',
              isDanger ? 'bg-red-500/10 text-red-500' : 'bg-primary/10 text-primary',
            )}
          >
            {icon ?? <AlertTriangle className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-foreground">{title}</h3>
            {description && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('Zatvoriť')}
            className={cn(BUTTON_GHOST, 'shrink-0')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {children && <div className="mb-4">{children}</div>}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
          >
            {/* FB-006: legacy defaults read "Zrusit"/"Potvrdit"/"Spracuvam..." -
                Slovak with the diacritics stripped. */}
            {cancelLabel ?? tt('Zrušiť')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading || confirmDisabled === true}
            className={cn(
              // §9.73 — both branches together, deliberately. The dialog's two buttons agreed with
              // each other and disagreed with every other button in the app: `font-semibold` where
              // the rest are bold, `transition-all` where the rest transition what they change.
              // Converting one branch would have fixed that by breaking the agreement that was
              // right; converting both keeps it and joins the rest.
              'flex-1 px-4 py-2.5 rounded-xl text-sm',
              isDanger ? BUTTON_DANGER : BUTTON_PRIMARY,
            )}
          >
            {loading ? tt('Spracúvam...') : (confirmLabel ?? tt('Potvrdiť'))}
          </button>
        </div>
      </div>
    </div>
  )
}
